import { cached } from '@/api/cache';
import {
  ApiError,
  isGuest,
  type Lang,
  type List,
  request,
  retryable,
  toRelativeMedia,
  withPicture,
} from '@/api/client';
import type { QuestionList } from '@/api/questions';
import { readStored, writeStored } from '@/storage';

export interface LessonSummary {
  topic: number;
  title: string;
  section_count: number;
  read: boolean;
}

export type LessonsList = List<LessonSummary>;

export interface LessonSection {
  position: number;
  heading: string;
  body: string;
  image_url: string | null;
}

export interface Lesson {
  topic: number;
  title: string;
  sections: LessonSection[];
}

export function getLessons(lang: Lang): Promise<LessonsList> {
  return request<LessonsList>(`/lessons?lang=${lang}`).then((list) => {
    const owed = readReadOutbox();
    return owed.length === 0
      ? list
      : {
          ...list,
          data: list.data.map((l) => (owed.includes(l.topic) ? { ...l, read: true } : l)),
        };
  });
}

export function getLesson(topic: number, lang: Lang): Promise<Lesson> {
  return request<Lesson>(`/lessons/${topic}?lang=${lang}`).then((lesson) => ({
    ...lesson,
    sections: lesson.sections.map((s) => ({ ...s, image_url: toRelativeMedia(s.image_url) })),
  }));
}

export function getLessonDrill(
  topic: number,
  lang: Lang,
  skip = 0,
  limit = 20,
): Promise<QuestionList> {
  return request<QuestionList>(
    `/lessons/${topic}/questions?lang=${lang}&skip=${skip}&limit=${limit}`,
  ).then((list) => ({
    ...list,
    data: list.data.map(withPicture),
  }));
}

export function markLessonRead(topic: number): Promise<void> {
  return request(`/lessons/${topic}/read`, { method: 'POST' });
}

const READ_OUTBOX_KEY = 'lesson_read_outbox';

function readReadOutbox(): number[] {
  try {
    const raw: unknown = JSON.parse(readStored(READ_OUTBOX_KEY) ?? '[]');
    return Array.isArray(raw) ? raw.filter((x): x is number => typeof x === 'number') : [];
  } catch {
    return [];
  }
}

function writeReadOutbox(topics: number[]): void {
  writeStored(READ_OUTBOX_KEY, JSON.stringify(topics));
}

let flushingReads = false;

export async function flushLessonReads(): Promise<void> {
  if (flushingReads || isGuest()) return;
  const owed = readReadOutbox();
  if (owed.length === 0) return;
  flushingReads = true;
  try {
    for (const topic of owed) {
      try {
        await markLessonRead(topic);
        writeReadOutbox(readReadOutbox().filter((t) => t !== topic));
      } catch (e) {
        if (!retryable(e)) writeReadOutbox(readReadOutbox().filter((t) => t !== topic));
      }
    }
  } finally {
    flushingReads = false;
  }
}

export function queueLessonRead(topic: number): void {
  const owed = readReadOutbox();
  if (!owed.includes(topic)) writeReadOutbox([...owed, topic]);
  void flushLessonReads();
}

export interface SignSummary {
  code: string;
  category: number;
  name: string;
  image_url: string | null;
  topic: number | null;
}

export interface SignOfTheDay extends SignSummary {
  choices: string[];
}

export type SignsList = List<SignSummary>;

export interface SignCategory {
  number: number;
  name: string;
  sign_count: number;
}

export type SignCategoriesList = List<SignCategory>;

export interface Sign extends SignSummary {
  description: string | null;
  simple_description?: string | null;
}

const SIGNS_TTL_MS = 5 * 60_000;

const signsCache = cached<SignsList>(SIGNS_TTL_MS);

const signCatsCache = cached<SignCategoriesList>(SIGNS_TTL_MS);

const signCache = cached<Sign>(SIGNS_TTL_MS);

const signOfDayCache = cached<SignOfTheDay | null>(SIGNS_TTL_MS);

export function invalidateContentCaches(): void {
  signsCache.invalidate();
  signCatsCache.invalidate();
  signCache.invalidate();
  signOfDayCache.invalidate();
  refCache.invalidate();
}

export function getSignCategories(lang: Lang): Promise<SignCategoriesList> {
  return signCatsCache.get(lang, () =>
    request<SignCategoriesList>(`/signs/categories?lang=${lang}`),
  );
}

export function getSignOfTheDay(lang: Lang): Promise<SignOfTheDay | null> {
  return signOfDayCache.get(lang, () =>
    request<SignSummary & { choices?: unknown }>(`/signs/today?lang=${lang}`).then(
      (s) => ({
        ...s,
        image_url: toRelativeMedia(s.image_url),
        choices: Array.isArray(s.choices)
          ? s.choices.filter((c): c is string => typeof c === 'string')
          : [],
      }),
      (e: unknown) => {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      },
    ),
  );
}

export function getSigns(lang: Lang, category?: number, limit = 500): Promise<SignsList> {
  const q = new URLSearchParams({ lang, limit: String(limit) });
  if (category != null) q.set('category', String(category));
  return signsCache.get(q.toString(), () =>
    request<SignsList>(`/signs?${q}`).then((list) => ({
      ...list,
      data: list.data.map((s) => ({ ...s, image_url: toRelativeMedia(s.image_url) })),
    })),
  );
}

export function getSign(code: string, lang: Lang): Promise<Sign> {
  return signCache.get(`${code}|${lang}`, () =>
    request<Sign>(`/signs/${code}?lang=${lang}`).then((s) => ({
      ...s,
      image_url: toRelativeMedia(s.image_url),
    })),
  );
}

export interface RuleSummary {
  position: number;
  number: string;
  title: string;
}

export interface Rule extends RuleSummary {
  body: string;
}

export interface ExamCenter {
  position: number;
  name: string;
  address: string;
  hours: string | null;
  transport: string | null;
  phones: string[];
  email: string | null;
  latitude: number | null;
  longitude: number | null;
}

const refCache = cached<unknown>(SIGNS_TTL_MS);

export const cachedRef = <T>(path: string): Promise<T> =>
  refCache.get(path, () => request<unknown>(path)) as Promise<T>;

export function getRules(lang: Lang, q?: string): Promise<List<RuleSummary>> {
  const p = new URLSearchParams({ lang });
  if (q) p.set('q', q);
  return cachedRef(`/rules?${p}`);
}

export function getRule(position: number, lang: Lang): Promise<Rule> {
  return cachedRef(`/rules/${position}?lang=${lang}`);
}

export interface CheatRow {
  value: string;
  unit: string;
  label: string;
}

export interface CheatGroup {
  heading: string;
  rows: CheatRow[];
}

export interface CheatSheet {
  position: number;
  icon: string | null;
  title: string;
  body: string;
  groups: CheatGroup[];
}

export function getCheatSheets(lang: Lang): Promise<List<CheatSheet>> {
  return cachedRef(`/cheat-sheets?lang=${lang}`);
}

export function getExamCenters(lang: Lang): Promise<List<ExamCenter>> {
  return cachedRef(`/exam-centers?lang=${lang}`);
}

export interface LibraryCounts {
  lessons: number;
  signs: number;
  cheat_sheets: number;
  rules: number;
  exam_centers: number;
}

export function getLibraryCounts(): Promise<LibraryCounts> {
  return cachedRef('/library/counts');
}
